/* Launchpad guide behaviour: open a step, tick it off, pick a path, and have
   all of that still be true when you come back tomorrow.

   Progress is stored per guide_key, which is why that key must not change
   once a guide is published. State is restored before the section is allowed
   to animate anything: a progress bar that slides from zero to 7 of 11 on
   every page load is the "loads then jumps into place" failure, and it also
   makes returning feel like starting again.

   Without JS the guide still reads: every step body is a plain block that
   the open button reveals, and nothing is hidden behind script except the
   conveniences. */
(function () {
  'use strict';

  function init(root) {
    var key = 'ak-guide:' + (root.dataset.guideKey || 'guide');
    var pathKey = key + ':path';

    var steps = [].slice.call(root.querySelectorAll('[data-ak-guide-step]'));
    var phases = [].slice.call(root.querySelectorAll('.ak-guide__phase'));
    var pathBtns = [].slice.call(root.querySelectorAll('[data-ak-guide-path]'));
    var doneEl = root.querySelector('[data-ak-guide-done]');
    var shownEl = root.querySelector('[data-ak-guide-shown]');
    var fill = root.querySelector('[data-ak-guide-fill]');
    var bar = root.querySelector('[data-ak-guide-bar]');
    var resetBtn = root.querySelector('[data-ak-guide-reset]');
    var donePanel = root.querySelector('[data-ak-guide-done-panel]');

    /* ---- storage, defensive: private mode throws on write ------------- */

    function read() {
      try {
        return JSON.parse(window.localStorage.getItem(key)) || [];
      } catch (e) {
        return [];
      }
    }

    function write(ids) {
      try {
        window.localStorage.setItem(key, JSON.stringify(ids));
      } catch (e) {
        /* progress just will not persist; the guide still works */
      }
    }

    function readPath() {
      try {
        return window.localStorage.getItem(pathKey) || '';
      } catch (e) {
        return '';
      }
    }

    function writePath(p) {
      try {
        window.localStorage.setItem(pathKey, p);
      } catch (e) {}
    }

    /* ---- the numbers -------------------------------------------------- */

    function visible(step) {
      var phase = step.closest('.ak-guide__phase');
      return !phase || !phase.hidden;
    }

    function refresh() {
      var shown = steps.filter(visible);

      // Numbers count what you can actually see. Leaving the authored order
      // in place made easy mode jump from 5 to 13 and read as broken.
      shown.forEach(function (step, i) {
        var n = step.querySelector('.ak-guide__step-n');
        if (n) n.textContent = String(i + 1);
      });

      var done = shown.filter(function (s) {
        return s.classList.contains('is-done');
      });
      var pct = shown.length ? Math.round((done.length / shown.length) * 100) : 0;

      if (doneEl) doneEl.textContent = String(done.length);
      if (shownEl) shownEl.textContent = String(shown.length);
      if (fill) fill.style.width = pct + '%';
      if (bar) bar.setAttribute('aria-valuenow', String(pct));
      if (resetBtn) resetBtn.hidden = done.length === 0;
      if (donePanel) donePanel.hidden = !(shown.length && done.length === shown.length);
      root.classList.toggle('is-complete', !!shown.length && done.length === shown.length);
    }

    /* ---- ticking ------------------------------------------------------ */

    var saved = read();

    steps.forEach(function (step) {
      var box = step.querySelector('[data-ak-guide-check]');
      var id = step.dataset.akGuideStep;
      if (!box) return;

      if (saved.indexOf(id) !== -1) {
        box.checked = true;
        step.classList.add('is-done');
      }

      box.addEventListener('change', function () {
        step.classList.toggle('is-done', box.checked);
        var ids = read();
        var at = ids.indexOf(id);
        if (box.checked && at === -1) ids.push(id);
        if (!box.checked && at !== -1) ids.splice(at, 1);
        write(ids);
        refresh();
      });
    });

    /* ---- open and close -----------------------------------------------
       Opening something has to leave the thing you opened where you can see
       it. Two layers can swallow it: the sticky progress rail, and the
       header when it is fixed and showing. So the visible top is measured
       from whatever is actually covering the page at that moment, and the
       row is only moved if it sits above that line. */

    var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    function coveredTop() {
      var rail = root.querySelector('[data-ak-guide-rail]');
      var railH = 0;
      if (rail) {
        var rr = rail.getBoundingClientRect();
        // only counts while it is actually stuck to the top
        if (rr.top <= 1) railH = rr.height;
      }
      var chrome = document.querySelector('.ak-chrome.is-fixed');
      var chromeH = 0;
      if (chrome && !chrome.classList.contains('is-tucked')) {
        chromeH = chrome.getBoundingClientRect().height;
      }
      return railH + chromeH;
    }

    function keepInView(el) {
      var min = coveredTop() + 12;
      var top = el.getBoundingClientRect().top;
      if (top >= min) return;
      window.scrollBy({
        top: top - min,
        left: 0,
        behavior: reduceMotion ? 'auto' : 'smooth'
      });
    }

    steps.forEach(function (step) {
      var btn = step.querySelector('[data-ak-guide-open]');
      var body = step.querySelector('.ak-guide__step-body');
      if (!btn || !body) return;
      btn.addEventListener('click', function () {
        var open = btn.getAttribute('aria-expanded') === 'true';
        btn.setAttribute('aria-expanded', open ? 'false' : 'true');
        body.hidden = open;
        step.classList.toggle('is-open', !open);
        // only when opening, and only if the row sits under the rail
        if (open) return;
        requestAnimationFrame(function () { keepInView(step); });
      });
    });

    root.querySelectorAll('.ak-guide__ref-item').forEach(function (item) {
      item.addEventListener('toggle', function () {
        if (item.open) requestAnimationFrame(function () { keepInView(item); });
      });
    });

    /* ---- paths and modes -----------------------------------------------
       A phase shows when its path matches AND its mode matches. Either can
       be blank, which means "always". The mode switch is rebuilt whenever
       the path changes, because the modes on offer belong to the path: the
       phyto cycle has an easy mode, the copepod one does not. */

    var modeWrap = root.querySelector('[data-ak-guide-modes]');
    var modeBtnWrap = root.querySelector('[data-ak-guide-mode-btns]');
    var currentPath = '';
    var currentMode = '';

    function modeKeyFor(p) { return key + ':mode:' + p; }

    function readMode(p) {
      try { return window.localStorage.getItem(modeKeyFor(p)) || ''; } catch (e) { return ''; }
    }

    function writeMode(p, m) {
      try { window.localStorage.setItem(modeKeyFor(p), m); } catch (e) {}
    }

    function inPath(phase, p) {
      var owned = phase.dataset.path;
      return !owned || owned === p;
    }

    /* A phase may belong to more than one mode: the first proper cycle is
       part of both "Every harvest" and "Easy mode", because easy mode only
       starts once you have a harvest to bank from. */
    function modesOf(phase) {
      var raw = phase.dataset.mode || '';
      // pipe separated, so a mode label may contain a comma
      return raw.split('|').map(function (x) { return x.trim(); }).filter(Boolean);
    }

    function modesFor(p) {
      var out = [];
      phases.forEach(function (phase) {
        if (!inPath(phase, p)) return;
        modesOf(phase).forEach(function (m) {
          if (out.indexOf(m) === -1) out.push(m);
        });
      });
      return out;
    }

    function applyFilter() {
      phases.forEach(function (phase) {
        var list = modesOf(phase);
        phase.hidden = !inPath(phase, currentPath) ||
                       (list.length > 0 && list.indexOf(currentMode) === -1);
      });
      pathBtns.forEach(function (b) {
        b.setAttribute('aria-pressed', b.dataset.akGuidePath === currentPath ? 'true' : 'false');
      });
      if (modeBtnWrap) {
        [].slice.call(modeBtnWrap.children).forEach(function (b) {
          b.setAttribute('aria-pressed', b.dataset.akGuideMode === currentMode ? 'true' : 'false');
        });
      }
      refresh();
    }

    function buildModes() {
      if (!modeWrap || !modeBtnWrap) return;
      var list = modesFor(currentPath);
      modeBtnWrap.textContent = '';
      modeWrap.hidden = list.length < 2;
      if (list.length < 2) {
        currentMode = list[0] || '';
        return;
      }
      var saved = readMode(currentPath);
      currentMode = list.indexOf(saved) !== -1 ? saved : list[0];
      list.forEach(function (m) {
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'ak-guide__path ak-guide__mode';
        b.dataset.akGuideMode = m;
        b.textContent = m;
        b.setAttribute('aria-pressed', m === currentMode ? 'true' : 'false');
        b.addEventListener('click', function () {
          currentMode = m;
          writeMode(currentPath, m);
          applyFilter();
        });
        modeBtnWrap.appendChild(b);
      });
    }

    function applyPath(p) {
      currentPath = p;
      buildModes();
      applyFilter();
    }

    var known = pathBtns.map(function (b) { return b.dataset.akGuidePath; });
    var start = readPath();
    if (known.indexOf(start) === -1) start = known[0] || '';
    applyPath(start);

    pathBtns.forEach(function (b) {
      b.addEventListener('click', function () {
        writePath(b.dataset.akGuidePath);
        applyPath(b.dataset.akGuidePath);
      });
    });

    /* ---- start over ---------------------------------------------------- */

    if (resetBtn) {
      resetBtn.addEventListener('click', function () {
        write([]);
        steps.forEach(function (step) {
          var box = step.querySelector('[data-ak-guide-check]');
          if (box) box.checked = false;
          step.classList.remove('is-done');
        });
        refresh();
        root.scrollIntoView({ block: 'start', behavior: 'smooth' });
      });
    }

    refresh();
    // only now may anything transition
    root.classList.add('is-ready');
  }

  function boot() {
    document.querySelectorAll('[data-ak-guide]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
